import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  Signal,
  computed,
  inject,
} from '@angular/core'
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop'
import {
  FormBuilder,
  ReactiveFormsModule,
  UntypedFormControl,
  UntypedFormGroup,
} from '@angular/forms'
import { ActivatedRoute, Router } from '@angular/router'

import { ColDef, GridApi, GridOptions } from 'ag-grid-community'
import { isEqual as _isEqual, isEmpty } from 'lodash/fp'
import { DatePickerModule } from 'primeng/datepicker'

import { BehaviorSubject } from 'rxjs'
import { debounceTime, distinctUntilChanged, filter, tap } from 'rxjs/operators'

import { FlxFormControlModule } from '@flowx/angular-ui-toolkit'
import { FlxResourcesGridComponent } from '@flowx/designer/modules/projects/components/resources-grid/resources-grid.component'
import { FlxDateCellComponent } from '@flowx/designer/shared/components/ag-grid/cell-components/date-cell.component'
import {
  ProcessException,
  ProcessExceptionsQueryParams,
} from '@flowx/designer/shared/entities/process-exceptions.types'
import { ProcessExceptionsService } from '@flowx/designer/shared/services/process-exceptions.service'
import { ProcessInstanceService } from '@flowx/designer/shared/services/process-instance.service'
import {
  ProcessApiParameters,
  UrlParametersStoreService,
} from '@flowx/designer/shared/services/url-parameters-store.service'

@Component({
  selector: 'app-exceptions',
  templateUrl: './exceptions-page.component.html',
  styleUrls: ['./exceptions-page.component.scss'],
  host: { class: 'h-full' },
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [DatePickerModule, FlxResourcesGridComponent, FlxFormControlModule, ReactiveFormsModule],
})
export class ExceptionsPageComponent implements OnDestroy {
  readonly #router = inject(Router)
  readonly #processExceptionsService = inject(ProcessExceptionsService)
  readonly #processInstanceService = inject(ProcessInstanceService)
  readonly #urlParametersStore = inject(UrlParametersStoreService)
  readonly #applicationProcesses = toSignal(this.#processInstanceService.getNames())
  readonly #activatedRoute = inject(ActivatedRoute)
  readonly #fb = inject(FormBuilder)

  gridOptions: Signal<GridOptions> = computed(() => ({
    rowModelType: 'infinite',
    datasource: this.#processExceptionsService.gridDataSource(),
  }))

  readonly exceptionsTableColumns: ColDef[] = [
    {
      headerName: 'Definition name',
      field: 'processDefinitionName',
      minWidth: 150,
    },
    {
      headerName: 'Message',
      field: 'message',
      minWidth: 75,
    },
    {
      headerName: 'Cause Type',
      field: 'causeType',
      minWidth: 90,
    },
    {
      headerName: 'Timestamp',
      field: 'date',
      width: 204,
      flex: 0,
      cellRenderer: FlxDateCellComponent,
    },
  ]

  exceptionsFilterFG = new UntypedFormGroup({
    definitionName: new UntypedFormControl(''),
  })

  dateRangeGroupFG = this.#fb.group({
    startDate: [],
    endDate: [],
  })

  startTime = new Date(new Date().setHours(0, 0, 0, 0))
  endTime = new Date(new Date().setHours(23, 59, 59, 999))

  filters: Record<string, string>

  processApiParameters: ProcessApiParameters = this.#urlParametersStore.urlParameters
  initialQuery = {
    applicationId: this.processApiParameters.appId,
  }
  processFilter$ = new BehaviorSubject<string>('')
  processFilter = toSignal(this.processFilter$.pipe(debounceTime(300)))

  filteredApplicationProcesses = computed(() => {
    const processFilter = this.processFilter()
    const applicationProcesses = this.#applicationProcesses()
    return applicationProcesses?.filter(
      (process) => process.resourceName.includes(processFilter) || isEmpty(processFilter)
    )
  })

  constructor() {
    this.exceptionsFilterFG.valueChanges
      .pipe(
        filter((filters) => filters.definitionName !== ''),
        tap(() => this.getExceptions()),
        takeUntilDestroyed()
      )
      .subscribe()

    this.dateRangeGroupFG.valueChanges
      .pipe(
        debounceTime(500),
        distinctUntilChanged(_isEqual),
        tap(() => this.getExceptions()),
        takeUntilDestroyed()
      )
      .subscribe()
  }

  getExceptions(): void {
    this.#processExceptionsService.search(this.buildFilters())
  }

  onGridReady(gridApi: GridApi): void {
    this.#processExceptionsService.setGridApi(gridApi)
  }

  navigateToException(exception: ProcessException): void {
    this.#router.navigate([exception.id], { relativeTo: this.#activatedRoute })
  }

  buildFilters(): Partial<ProcessExceptionsQueryParams> {
    const { definitionName } = this.exceptionsFilterFG.value
    const { startDate, endDate } = this.dateRangeGroupFG.value

    return {
      ...this.initialQuery,
      ...(startDate && { fromDate: new Date(startDate).toISOString() }),
      ...(endDate && { beforeDate: new Date(endDate).toISOString() }),
      ...(definitionName?.resourceDefinitionId && {
        resourceDefinitionId: definitionName.resourceDefinitionId,
      }),
    }
  }

  ngOnDestroy(): void {
    this.#processExceptionsService.reset()
  }
}
